import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";

import BaseView from "../base-view/BaseView";
import AssetDetails from "../../components/asset-details/AssetDetails";
import CommonConstants from "../../constants/CommonConstants";
import SearchSidebar from "../../components/search-sidebar/SearchSidebar";
import Grid from "../../components/grid/Grid";
import Loading from "../../components/loading/Loading";
import Thumbnail from "../../components/thumbnail/Thumbnail";
import NuxeoClient from "../../clients/NuxeoClient";
import EventUtils from "../../utils/EventUtils";
import ObjectUtils from "../../utils/ObjectUtils";
import SearchResults from "../../models/SearchResults";

import "./AssetsView.css";
import ReportSection from "./ReportSection";

const ASSET_DETAIL_LINK_FORMATTER = (cell, entry) => (
  <Link to={AssetsView.getPath({ id: entry.uid })}>{cell}</Link>
);

const ASSET_THUMBNAIL_FORMATTER = (cell) => <Thumbnail image={cell} />;

const ARCHIVE_GRID_COLUMNS = [
  {
    displayName: "Submission ID",
    propertyName: "properties.archive_file:archiveSubmissionId",
  },
  {
    propertyName: "properties.picture:views[0].content.data",
    formatter: ASSET_THUMBNAIL_FORMATTER,
  },
  {
    displayName: "File Name",
    propertyName: "title",
    formatter: ASSET_DETAIL_LINK_FORMATTER,
  },
  {
    displayName: "Property Name",
    propertyName: "properties.archive_submission:propertyName",
  },
  {
    displayName: "Licensee Name",
    propertyName: "properties.archive_submission:licenseeName",
  },
  {
    displayName: "Product Category Name",
    propertyName: "properties.archive_submission:productCategoryName",
  },
  {
    displayName: "Product Detail Name",
    propertyName: "properties.archive_submission:productDetailName",
  },
];

const ACTIVE_GRID_COLUMNS = [
  {
    displayName: "Submission ID",
    propertyName: "properties.live_file:workfrontProjectJobId",
  },
  {
    propertyName: "properties.picture:views[0].content.data",
    formatter: ASSET_THUMBNAIL_FORMATTER,
  },
  {
    displayName: "File Name",
    propertyName: "title",
    formatter: ASSET_DETAIL_LINK_FORMATTER,
  },
  {
    displayName: "Property Name",
    propertyName: "properties.submission:propertyName",
  },
  {
    displayName: "Licensee Name",
    propertyName: "properties.submission:licenseeName",
  },
  {
    displayName: "Product Category Name",
    propertyName: "properties.submission:productCategoryName",
  },
  {
    displayName: "Product Detail Name",
    propertyName: "properties.submission:productDetailName",
  },
];

const ASSET_THUMBNAIL_MAPPER = (entry) => {
  return {
    href: AssetsView.getPath({ id: entry.uid }),
    thumbnail: entry.properties["picture:views"][
      CommonConstants.FILE_VIEWS_THUMBNAIL
    ]
      ? entry.properties["picture:views"][CommonConstants.FILE_VIEWS_THUMBNAIL]
          .content.data
      : null,
    text: entry.title,
    actions: {
      glamour: {
        icon: "grade",
        name: "Is Glamour",
        isChecked: (data) => {
          const galmourValue =
            data.type === CommonConstants.ARCHIVE_ASSETS_FILE_TYPE
              ? data.properties["archive_file:archiveIsGlamour"]
              : data.properties["live_file:liveIsGlamour"];
          return galmourValue;
        },
        clickHandler: (event) => {
          let actionButton = event.target.parentElement;

          const { data } = event.detail;
          const checked = !actionButton.classList.contains("checked");
          const glamourProperty =
            data.type === CommonConstants.ARCHIVE_ASSETS_FILE_TYPE
              ? { "archive_file:archiveIsGlamour": `${checked}` }
              : { "live_file:liveIsGlamour": `${checked}` };

          NuxeoClient.instance().updateFile(
            data.uid,
            glamourProperty,
            data.type
          );

          if (checked) {
            actionButton.classList.add("checked");
          } else {
            actionButton.classList.remove("checked");
          }
        },
      },
      trademark: {
        icon: "work",
        name: "Is Trademark",
        isChecked: (data, type) => {
          const trademarkValue =
            data.type === CommonConstants.ARCHIVE_ASSETS_FILE_TYPE
              ? data.properties["archive_file:archiveIsTrademark"]
              : data.properties["live_file:liveIsTrademark"];
          return trademarkValue;
        },
        clickHandler: (event) => {
          let actionButton = event.target.parentElement;

          const { data } = event.detail;
          const checked = !actionButton.classList.contains("checked");
          const trademarkProperty =
            data.type === CommonConstants.ARCHIVE_ASSETS_FILE_TYPE
              ? { "archive_file:archiveIsTrademark": `${checked}` }
              : { "live_file:liveIsTrademark": `${checked}` };

          NuxeoClient.instance().updateFile(
            data.uid,
            trademarkProperty,
            data.type
          );

          if (checked) {
            actionButton.classList.add("checked");
          } else {
            actionButton.classList.remove("checked");
          }
        },
      },
    },
  };
};

class AssetsView extends BaseView {
  static VIEW_PATH = "/asset";

  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      page: CommonConstants.FILTER_FIRST_PAGE,
      searchTerm: null,
      searchResults: new SearchResults(),
      selectedAsset: null,
      assetType: null,
      selectedReport: false,
      reportType: "",
    };

    this.activeReponse = null;
    this.isActiveSelected = false;

    this.archiveReponse = null;
    this.isArchivedSelected = false;

    this.loadingtimer = null;

    this._loadMoreSearchResults = this._loadMoreSearchResults.bind(this);
    this._loadSelectedAsset = this._loadSelectedAsset.bind(this);
    this._handleAddToCart = this._handleAddToCart.bind(this);
    this._reportSelectionResults = this._reportSelectionResults.bind(this);
    this._checkInitActiveResultsLoaded = this._checkInitActiveResultsLoaded.bind(
      this
    );
    this._checkInitArchiveResultsLoaded = this._checkInitArchiveResultsLoaded.bind(
      this
    );
    this._handleInitReportResult = this._handleInitReportResult.bind(this);
  }

  get loading() {
    return this.state.loading;
  }

  set loading(loading) {
    this.setState({ loading: loading });
  }

  get page() {
    return this.state.page;
  }

  set page(page) {
    this.setState({ page: page });
  }

  get searchResults() {
    return this.state.searchResults;
  }

  set searchResults(searchResults) {
    this.setState({ searchResults: searchResults });
  }

  get searchTerm() {
    return this.state.searchTerm;
  }

  set searchTerm(searchTerm) {
    this.setState(
      {
        searchTerm: searchTerm,
        page: CommonConstants.FILTER_FIRST_PAGE,
        searchResults: new SearchResults(),
        selectedAsset: null,
        assetType: null,
      },
      () => this._loadMoreSearchResults()
    );
  }

  get selectedAsset() {
    return this.state.selectedAsset;
  }

  set selectedAsset(selectedAsset) {
    this.setState({ selectedAsset: selectedAsset });
  }

  get assetType() {
    return this.state.assetType;
  }

  set assetType(assetType) {
    this.setState({ assetType: assetType });
  }

  get selectedReport() {
    return this.state.selectedReport;
  }

  set selectedReport(selectedReport) {
    this.setState({ selectedReport: selectedReport });
  }

  get reportType() {
    return this.state.reportType;
  }

  set reportType(reportType) {
    this.setState({ reportType: reportType });
  }

  componentDidMount() {
    //Load Active and Archive Result initialy
    this._loadActiveResults();
    this._loadArchiveResults();
    this._loadSelectedAsset(this.props.location);

    this._handleClearToCart(); //Clear Cart item when page change
  }

  componentWillReceiveProps(props) {
    if (this.props.location.search !== props.location.search) {
      this._loadSelectedAsset(props.location);
    }
  }

  _handleClearToCart() {
    // Clear the cart item when change page
    EventUtils.raiseEvent("clear-to-cart", { items: [] }, this.props);
  }

  async _reportSelectionResults(event) {
    await this.setState({ selectedReport: true, reportType: event.detail });
    this._searchSidebar._handleClearClick(); //Clear everything before load...

    this._handleInitReportResult();
  }

  _handleInitReportResult() {
    //Clear the time out
    this.clearTimer();

    if (this.state.reportType === "Active") {
      this.loading = true;
      this._checkInitActiveResultsLoaded();
    }
    if (this.state.reportType === "Archived") {
      this.loading = true;
      this._checkInitArchiveResultsLoaded();
    }
  }

  clearTimer() {
    if (this.loadingtimer) {
      clearTimeout(this.loadingtimer);
      this.loadingtimer = null;
    }
  }

  _checkInitActiveResultsLoaded() {
    //Again check reponse stored, else show loading
    if (this.activeReponse === null) {
      this.loading = true;
      this.isActiveSelected = true;
    } else {
      //Hereby go, if already Active result stored
      this.loadingtimer = setTimeout(() => {
        this.setState({ loading: false });
      }, 2000);
      this.setState(
        {
          searchResults: new SearchResults(this.activeReponse),
        },
        () =>
          (this._searchSidebar.aggregations = this.state.searchResults.aggregations)
      );
    }
  }

  _checkInitArchiveResultsLoaded() {
    //Again check reponse stored, else show loading
    if (this.archiveReponse === null) {
      this.loading = true;
      this.isArchivedSelected = true;
    } else {
      //Hereby go, if already Archive result stored
      this.loadingtimer = setTimeout(() => {
        this.setState({ loading: false });
      }, 2000);
      this.setState(
        {
          searchResults: new SearchResults(this.archiveReponse),
        },
        () =>
          (this._searchSidebar.aggregations = this.state.searchResults.aggregations)
      );
    }
  }

  _loadActiveResults() {
    const { searchTerm, page } = this.state;

    NuxeoClient.instance()
      .getLiveSubmissionFiles(
        page,
        CommonConstants.FILTER_MIN_PAGE_SIZE,
        null,
        null,
        searchTerm
      )
      .then((response) => {
        response.entries = [];
        this.activeReponse = response;

        if (this.isActiveSelected) {
          this.isActiveSelected = false;
          this.setState(
            {
              loading: false,
              searchResults: new SearchResults(response),
            },
            () =>
              (this._searchSidebar.aggregations = this.state.searchResults.aggregations)
          );
        }
      })
      .catch((error) => {
        window.Materialize.toast("Failed to retrive filters.", 5000);
        this.loading = false;
      });
  }

  _loadArchiveResults() {
    const { searchTerm, page } = this.state;

    NuxeoClient.instance()
      .getArchivedFiles(
        page,
        CommonConstants.FILTER_MIN_PAGE_SIZE,
        null,
        null,
        searchTerm
      )
      .then((response) => {
        response.entries = [];
        this.archiveReponse = response;

        if (this.isArchivedSelected) {
          this.isArchivedSelected = false;
          this.setState(
            {
              loading: false,
              searchResults: new SearchResults(response),
            },
            () =>
              (this._searchSidebar.aggregations = this.state.searchResults.aggregations)
          );
        }
      })
      .catch(() => {
        window.Materialize.toast("Failed to retrive filters.", 5000);
        this.loading = false;
      });
  }

  _loadMoreSearchResults() {
    const { searchTerm, page, searchResults, reportType } = this.state;
    this.loading = true;

    if (ObjectUtils.includesNotBlankProperty(searchTerm)) {
      NuxeoClient.instance()
        .getAssetsReportFiles(page, null, null, null, searchTerm, reportType)
        .then((response) => {
          this.setState(
            {
              loading: false,
              page: page + 1,
              searchResults: new SearchResults(response, searchResults.entries),
            },
            () =>
              (this._searchSidebar.aggregations = this.state.searchResults.aggregations)
          );
        })
        .catch(() => {
          window.Materialize.toast("Failed to retrive results.", 5000);
          this.loading = false;
        });
    } else {
      // A call just to get the initial aggregations also no search term
      //loading initial results of Active and Archive
      this._handleInitReportResult();
    }
  }

  _loadSelectedAsset(location) {
    const { id } = this.getSearch(location);
    const { reportType } = this.state;
    const liveProps = "live_file, file, submission, picture";
    const archivedProps =
      "archive_file, file, picture, archive_submission, dublincore";

    if (id) {
      this.loading = true;

      NuxeoClient.instance()
        // .getFile(id, 'archive_file, file, picture, archive_submission')
        // .getLiveFiles(id, 'file, picture')
        .getSelectedAssetFiles(id, liveProps, archivedProps, reportType)
        .then((file) => {
          this.selectedAsset = file ? file.properties : null;
          this.assetType = file ? file.type : null;
          this.loading = false;
        })
        .catch(() => {
          window.Materialize.toast("Failed to retrive asset.", 5000);
          this.loading = false;
        });
    } else {
      this.selectedAsset = null;
      this.assetType = null;
    }
  }

  _handleAddToCart(event) {
    EventUtils.raiseEvent(
      "add-to-cart",
      {
        items: event.detail.items,
      },
      this.props
    );
  }

  render() {
    const {
      loading,
      searchResults,
      selectedAsset,
      assetType,
      selectedReport,
      reportType,
    } = this.state;
    const jobType = [
      {
        label: "Active",
        value: "Active",
      },
      {
        label: "Archived",
        value: "Archived",
      },
    ];
    const GRID_COLUMNS =
      reportType === "Archived" ? ARCHIVE_GRID_COLUMNS : ACTIVE_GRID_COLUMNS;
    return (
      <div>
        <ReportSection
          options={jobType}
          selected={reportType}
          onChange={this._reportSelectionResults}
        />{" "}
        {selectedReport ? (
          <section className="assets-view">
            <Loading hidden={!loading} />
            <SearchSidebar
              reference={(element) => (this._searchSidebar = element)}
              onSearch={(event) => (this.searchTerm = event.detail)}
              jobType={reportType}
            />
            <div className="content">
              <AssetDetails asset={selectedAsset} type={assetType} />
              <Grid
                entries={searchResults.entries}
                totalCount={searchResults.totalCount}
                tableColumns={GRID_COLUMNS}
                thumbnailMapper={ASSET_THUMBNAIL_MAPPER}
                displayMode={Grid.DISPLAY_MODE_THUMBNAIL_GRID}
                onScrollBottom={this._loadMoreSearchResults}
                hidden={selectedAsset}
                onAddToCart={this._handleAddToCart}
              />
            </div>
          </section>
        ) : null}
      </div>
    );
  }

  componentWillUnmount() {
    //Clear the time out for loader
    this.clearTimer();
  }
}

export default withRouter(AssetsView);
